<template>
  <header>
    <!-- Background image header -->
    <div class="bgd-image bgd-image-short"></div>
  </header>
  
  <div class="content">
    <div class="container">
      <div class="row">
        <div class="col-lg-8 col-md-8 col-sm-12 col-xs-12">
          <!-- Blue well with neighborhood name -->
          <div class="well well-blue well-location">
            <div class="row">
              <div class="col-xs-8">
                <h1>Hey <strong>{{ neighborhoodName }}!</strong></h1>
              </div>
              <div class="col-xs-4">
                <div class="btn-group change-location">
                  <button type="button" class="btn btn-default dropdown-toggle change-location-button">
                    Change Location <span class="caret caret-white"></span>
                  </button>
                  <ul class="dropdown-menu change-location-dropdown" role="menu">
                    <li><a href="/neighborhoods" title="Browse Neighborhoods">Browse All Neighborhoods</a></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Main content section -->
          <section class="leftcontent newsfeed social-newsfeed">
            <!-- Inactive Neighborhood -->
            <div v-if="neighborhoodExists && !neighborhoodActive" class="inactive-neighborhood-message">
              <div class="inactive-neighborhood-content">
                <h3>Neighborhood Inactive</h3>
                <p>We have deactivated {{ neighborhoodName }} in {{ cityDisplayName }}, {{ stateDisplayName }} to focus on our core neighborhoods. Soon, there will be a way to pre-register for new neighborhoods.</p>
                <router-link to="/neighborhoods" class="btn-browse-neighborhoods">Browse Active Neighborhoods</router-link>
              </div>
            </div>

            <!-- Neighborhood Not Found -->
            <div v-else-if="neighborhoodNotFound" class="not-found-neighborhood-message">
              <div class="not-found-neighborhood-content">
                <h3>Neighborhood Not Found</h3>
                <p>We couldn't find {{ neighborhoodName }} in {{ cityDisplayName }}, {{ stateDisplayName }}. This neighborhood may not be available yet.</p>
                <router-link to="/neighborhoods" class="btn-browse-neighborhoods">Browse Available Neighborhoods</router-link>
              </div>
            </div>

            <!-- Loading state while checking neighborhood -->
            <div v-else-if="loading" class="loading-neighborhood">
              <div class="loading-spinner"></div>
              <p>Loading neighborhood...</p>
            </div>

            <!-- Active Neighborhood - Show Feed -->
            <UniversalFeed
              v-else
              :key="neighborhoodPath"
              :filter="neighborhoodFilter"
              sortBy="createdAt"
              sortDirection="DESC"
              :limit="15"
              paginationType="infinite"
              :showPostCreation="true"
              timeFormat="relative"
              :title="null"
              @show-auth="$emit('show-auth')"
            />
          </section>
        </div>

        <!-- Right sidebar -->
        <div class="col-lg-4 col-md-4 col-sm-12 col-xs-12">
          <aside class="sidebar newsfeed">
            <!-- Neighborhood Info Widget -->
            <div class="widget widget-neighborhood-info">
              <h4 class="widget-title">Neighborhood Info</h4>
              <div class="info-item">
                <strong>Neighborhood:</strong> {{ neighborhoodName }}
              </div>
              <div class="info-item">
                <strong>City:</strong> 
                <router-link :to="`/neighborhood/${state}/${city}`" class="info-link">
                  {{ cityDisplayName }}
                </router-link>
              </div>
              <div class="info-item">
                <strong>State:</strong> 
                <router-link :to="`/state/${state}`" class="info-link">
                  {{ stateDisplayName }}
                </router-link>
              </div>
              <div v-if="neighborhoodZipcodes.length > 0" class="info-item">
                <strong>ZIP Codes:</strong>
                <div class="zipcode-list">
                  <router-link 
                    v-for="zipcode in neighborhoodZipcodes" 
                    :key="zipcode"
                    :to="`/zipcode/${zipcode}`" 
                    class="zipcode-link"
                  >
                    {{ zipcode }}
                  </router-link>
                </div>
              </div>
            </div>
            
            <div class="widget widget-advert">
              <h5 class="tip">Advertisement</h5>
              <!-- Google AdSense Sidebar Ad -->
              <ins class="adsbygoogle"
                   style="display:block"
                   data-ad-client="ca-pub-1179423790307792"
                   data-ad-slot="5359564777"
                   data-ad-format="auto"
                   data-full-width-responsive="true"></ins>
            </div>
          </aside>
        </div>
      </div>
    </div>
    
    <AppFooter />
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';
import { computed } from 'vue';
import UniversalFeed from './shared/UniversalFeed.vue';
import AppFooter from './shared/AppFooter.vue';
import { getNeighborhoodByPath } from '../utils/referenceDataUtils.js';

export default {
  name: 'NeighborhoodFeed',
  components: {
    UniversalFeed,
    AppFooter
  },
  setup(props) {
    // Computed values for meta tags
    const neighborhoodName = computed(() => {
      return props.slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    });

    const cityName = computed(() => {
      return props.city
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    });

    const stateName = computed(() => props.state.toUpperCase());

    // Dynamic meta tags for SEO
    useHead({
      title: computed(() => `${neighborhoodName.value} - ${cityName.value}, ${stateName.value} | SocialHood`),
      meta: [
        {
          name: 'description',
          content: computed(() =>
            `Connect with neighbors in ${neighborhoodName.value}, ${cityName.value}, ${stateName.value}. Share local news, events, and community updates on SocialHood. Your neighborhood just got friendly!`
          )
        },
        // Open Graph tags for social sharing
        {
          property: 'og:title',
          content: computed(() => `${neighborhoodName.value} - ${cityName.value}, ${stateName.value}`)
        },
        {
          property: 'og:description',
          content: computed(() =>
            `Join the ${neighborhoodName.value} community. Connect with neighbors and stay updated on local news and events.`
          )
        },
        {
          property: 'og:type',
          content: 'website'
        }
      ]
    });

    return {};
  },
  props: {
    state: {
      type: String,
      required: true
    },
    city: {
      type: String,
      required: true
    },
    slug: {
      type: String,
      required: true
    },
    user: {
      type: Object,
      default: null
    },
    userAttributes: {
      type: Object,
      default: null
    }
  },
  emits: ['show-auth', 'neighborhoodSelected', 'userUpdated'],
  data() {
    return {
      neighborhoodData: null,
      loading: true, // Start as true, will be set to false after loadNeighborhoodData
      neighborhoodExists: false,
      neighborhoodActive: true,
      neighborhoodNotFound: false
    };
  },
  mounted() {
    this.loadNeighborhoodData().then(() => {
      this.initializeAds();
    });
  },
  computed: {
    neighborhoodPath() {
      // Convert route params to neighborhood path format with proper capitalization
      const capitalizedSlug = this.slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      const capitalizedCity = this.city
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Capitalize state: first letter uppercase, rest lowercase
      const capitalizedState = this.state.charAt(0).toUpperCase() + this.state.slice(1).toLowerCase();

      return `${capitalizedState}/${capitalizedCity}/${capitalizedSlug}`;
    },
    
    neighborhoodFilter() {
      return {
        neighborhood: {
          eq: this.neighborhoodPath
        }
      };
    },
    
    neighborhoodName() {
      return this.slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    
    cityDisplayName() {
      return this.city
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    
    stateDisplayName() {
      return this.state.toUpperCase();
    },
    
    neighborhoodZipcodes() {
      return this.neighborhoodData?.zipcodes || [];
    }
  },
  methods: {
    async loadNeighborhoodData() {
      try {
        this.loading = true;
        this.neighborhoodExists = false;
        this.neighborhoodActive = true;
        this.neighborhoodNotFound = false;

        const data = await getNeighborhoodByPath(this.state, this.city, this.slug);

        if (data) {
          this.neighborhoodData = data;
          this.neighborhoodExists = true;
          this.neighborhoodActive = data.active !== false; // Default to true if undefined
        } else {
          // Neighborhood not found
          this.neighborhoodNotFound = true;
        }
      } catch (error) {
        console.error('Error loading neighborhood data:', error);
        this.neighborhoodData = null;
        // If 404 or similar, it's not found
        if (error.message?.includes('404') || error.message?.includes('not found')) {
          this.neighborhoodNotFound = true;
        } else {
          // Other errors - treat as not found for now
          this.neighborhoodNotFound = true;
        }
      } finally {
        this.loading = false;
      }
    },

    initializeAds() {
      // Initialize Google AdSense ads after browser is idle (non-blocking)
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          try {
            if (typeof window !== 'undefined' && window.adsbygoogle) {
              (window.adsbygoogle = window.adsbygoogle || []).push({});
            }
          } catch (error) {
            // AdSense initialization failed
            console.warn('AdSense initialization failed:', error);
          }
        }, { timeout: 2000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          try {
            if (typeof window !== 'undefined' && window.adsbygoogle) {
              (window.adsbygoogle = window.adsbygoogle || []).push({});
            }
          } catch (error) {
            // AdSense initialization failed
            console.warn('AdSense initialization failed:', error);
          }
        }, 1000);
      }
    }
  }
};
</script>

<style scoped>
/* Use global CSS classes from style.css for proper layout */
/* Only add component-specific overrides here if needed */

/* Remove background image from header */
.bgd-image.bgd-image-short {
  background: none !important;
}

/* Neighborhood Info Widget */
.widget-neighborhood-info {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
}

.widget-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #f8f9fa;
}

.info-item {
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.4;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item strong {
  color: #495057;
  font-weight: 600;
  min-width: 90px;
  display: inline-block;
}

.info-link {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.info-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

.zipcode-list {
  margin-top: 5px;
}

.zipcode-link {
  display: inline-block;
  background: #f8f9fa;
  color: #007bff;
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 4px;
  margin-right: 8px;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
}

.zipcode-link:hover {
  background: #007bff;
  color: white;
  text-decoration: none;
}

/* Override widget-advert styles for AdSense */
.widget-advert {
  min-height: auto !important;
  padding: 15px !important;
  background: transparent !important;
}

/* Inactive Neighborhood Message */
.inactive-neighborhood-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.inactive-neighborhood-content {
  text-align: center;
  padding: 50px 40px;
  background: linear-gradient(135deg, #fff3cd 0%, #ffc107 100%);
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border: 2px solid #ffc107;
  max-width: 600px;
}

.inactive-neighborhood-content h3 {
  font-size: 28px;
  color: #856404;
  margin-bottom: 20px;
  font-weight: 700;
}

.inactive-neighborhood-content p {
  font-size: 18px;
  color: #856404;
  margin-bottom: 30px;
  line-height: 1.6;
}

.btn-browse-neighborhoods {
  display: inline-block;
  padding: 12px 30px;
  background-color: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-browse-neighborhoods:hover {
  background-color: #0056b3;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

/* Not Found Neighborhood Message */
.not-found-neighborhood-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.not-found-neighborhood-content {
  text-align: center;
  padding: 50px 40px;
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border: 2px solid #f5c6cb;
  max-width: 600px;
}

.not-found-neighborhood-content h3 {
  font-size: 28px;
  color: #721c24;
  margin-bottom: 20px;
  font-weight: 700;
}

.not-found-neighborhood-content p {
  font-size: 18px;
  color: #721c24;
  margin-bottom: 30px;
  line-height: 1.6;
}

/* Loading Neighborhood State */
.loading-neighborhood {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>